import { useCallback, useState } from "react";
import { useQuery } from "@apollo/client";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import {
  Assignment,
  Close,
  FilterList,
  PersonOutline,
  PlayCircleFilled,
  Refresh,
  ThumbDown,
  ThumbUp,
} from "@mui/icons-material";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { GET_ALL_EMPLOYEES } from "../../apollo/queries/processes";
import {
  createEmployee,
  getEmployee,
  resetError,
  resetSuccess,
  selectProcesses,
} from "../process/processSlices";
import {
  selectPreference,
  toggleThis,
} from "../preferences/preferencesSlice";
import { selectAuth } from "../auth/authSlice";
import { Role } from "../auth/types/authType";
import { isPrivilegedTransaction } from "../../utils/authUtils";
import CustomDialog from "../../components/modals/CustomDialog";
import Search from "../../components/search/Search";
import FilterSidebar from "../../components/filter/FilterSidebar";
import { VisaEntry } from "../process/VisaEntry";
import {
  Employee,
  EmployeeStatus,
  EmployeesWithCount,
  NotificationMessageTypes,
} from "../process/types/processTypes";

/** Stages before an employee is contracted. */
const PRE_CONTRACT_STATUSES = [EmployeeStatus.new, EmployeeStatus.selected];

const refreshSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

function ageFromDob(dob?: Date): number | undefined {
  if (!dob) return undefined;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return undefined;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age >= 0 && age < 120 ? age : undefined;
}

export default function EmployeeVideos() {
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const { selectedEmployee } = useAppSelector(selectProcesses);
  const {
    isFilterOpen,
    searchText,
    nationality,
    maritalStatus,
    religion,
    profession,
    arabic,
    amharic,
    english,
    ageStart,
    ageEnd,
    experienceYears,
  } = useAppSelector(selectPreference);
  const [selected, setSelected] = useState<Employee | null>(null);
  const [openVisa, setOpenVisa] = useState(false);

  const { data, loading, refetch } = useQuery<{ employees: EmployeesWithCount }>(
    GET_ALL_EMPLOYEES,
    {
      variables: {
        statuses: PRE_CONTRACT_STATUSES,
        skip: 0,
        take: 200,
        searchText:
          searchText && searchText.length > 0 ? searchText : undefined,
        nationality,
        maritalStatus,
        religion,
        profession,
        arabic,
        amharic,
        english,
        ageStart,
        ageEnd,
        experienceYears,
      },
      fetchPolicy: "cache-and-network",
    },
  );

  const videos = (data?.employees?.employees ?? []).filter(
    (e) => !!e.employeeVideo,
  );

  const roles = user?.roles as Role[];
  const canSelect = isPrivilegedTransaction(roles, "ProcessControl.Select");
  const canDeselect = isPrivilegedTransaction(roles, "ProcessControl.Deselect");

  const fullName = (e: Employee) =>
    [e.givenName, e.surName].filter(Boolean).join(" ") || "—";

  /** Age · marital status · religion on a single caption-less line. */
  const summaryLine = (e: Employee) => {
    const parts: string[] = [];
    const age = ageFromDob(e.dateOfBirth);
    if (age !== undefined) parts.push(`${age} years`);
    if (e.additionalData?.maritalStatus)
      parts.push(te(e.additionalData.maritalStatus));
    if (e.additionalData?.religion) parts.push(te(e.additionalData.religion));
    return parts.join(" · ");
  };

  /** Experience formatted like the /cvs list table's workExperience column. */
  const experience = (e: Employee) =>
    !e.workExperience?.length
      ? t("firstTimer")
      : e.workExperience
          .map((exp) =>
            [exp.country, exp.years]
              .filter(Boolean)
              .map((v) => te(v as string))
              .join(" - "),
          )
          .filter(Boolean)
          .join(", ");

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const toggleFilter = () => {
    dispatch(toggleThis({ type: "Filter", newValue: !isFilterOpen }));
  };

  /** Set status to `selected` and attach the acting partner company. */
  const handleSelect = async (employee: Employee) => {
    const loaded = await dispatch(getEmployee(employee._id as string));
    if (!getEmployee.fulfilled.match(loaded)) return;
    const values: Employee = {
      ...(loaded.payload as Employee),
      status: EmployeeStatus.selected,
      notificationMessage: NotificationMessageTypes.selected as string,
      partner: user?.company,
    };
    const result = await dispatch(createEmployee(values));
    if (createEmployee.fulfilled.match(result)) refetch();
  };

  /** Revert back to `new` and detach the visa/partner. */
  const handleDeselect = async (employee: Employee) => {
    const loaded = await dispatch(getEmployee(employee._id as string));
    if (!getEmployee.fulfilled.match(loaded)) return;
    const values: Employee = {
      ...(loaded.payload as Employee),
      status: EmployeeStatus.new,
      notificationMessage: NotificationMessageTypes.deSelected as string,
      clearVisa: true,
    };
    const result = await dispatch(createEmployee(values));
    if (createEmployee.fulfilled.match(result)) refetch();
  };

  /** Open the visa-assignment dialog to draw up the agreement. */
  const handleAddAgreement = async (employee: Employee) => {
    dispatch(resetSuccess());
    dispatch(resetError());
    const loaded = await dispatch(getEmployee(employee._id as string));
    if (!getEmployee.fulfilled.match(loaded)) return;
    setOpenVisa(true);
  };

  const closeVisa = useCallback(() => {
    dispatch(resetSuccess());
    dispatch(resetError());
    setOpenVisa(false);
    refetch();
  }, [dispatch, refetch]);

  return (
    <Box sx={{ m: { xs: 2, md: 4 }, py: 2 }}>
      <Helmet>
        <title>Employee Videos</title>
      </Helmet>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
      >
        <Tooltip title={t("refresh")}>
          <span>
            <Button
              color="secondary"
              variant="contained"
              disableElevation
              onClick={handleRefresh}
              disabled={loading}
              startIcon={
                <Refresh
                  sx={{
                    animation: loading
                      ? `${refreshSpin} 0.8s linear infinite`
                      : "none",
                  }}
                />
              }
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}
            >
              {t("refresh")}
            </Button>
          </span>
        </Tooltip>
        <Box
          sx={{ flexGrow: 1, display: "flex", justifyContent: "center" }}
        >
          <Search initialValue={searchText} />
        </Box>
        <Tooltip title={t("filter")}>
          <Button color="secondary" variant="contained" onClick={toggleFilter}>
            <FilterList />
          </Button>
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {loading && videos.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : videos.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ py: 6, textAlign: "center" }}
            >
              No employee videos available yet.
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {videos.map((employee) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={employee._id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardActionArea
                      onClick={() => setSelected(employee)}
                      sx={{ flexGrow: 1, alignItems: "stretch" }}
                    >
                      <Box sx={{ position: "relative" }}>
                        {employee.facePhoto ? (
                          <CardMedia
                            component="img"
                            image={employee.facePhoto}
                            alt={fullName(employee)}
                            sx={{ height: 200, objectFit: "cover" }}
                          />
                        ) : (
                          <Box
                            sx={{
                              height: 200,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: "action.hover",
                            }}
                          >
                            <PersonOutline
                              sx={{ fontSize: 64, color: "text.disabled" }}
                            />
                          </Box>
                        )}
                        <PlayCircleFilled
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            fontSize: 56,
                            color: "rgba(255,255,255,0.9)",
                            filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.6))",
                          }}
                        />
                      </Box>
                      <CardContent>
                        <Typography variant="subtitle1" noWrap>
                          {fullName(employee)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                        >
                          {summaryLine(employee)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          title={experience(employee)}
                        >
                          {experience(employee)}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                    {(canSelect || canDeselect) && (
                      <CardActions
                        sx={{
                          px: 2,
                          pb: 2,
                          pt: 1.5,
                          gap: 1,
                          flexWrap: "wrap",
                          borderTop: 1,
                          borderColor: "divider",
                        }}
                      >
                        {employee.status === EmployeeStatus.new &&
                          canSelect && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<ThumbUp />}
                              onClick={() => handleSelect(employee)}
                            >
                              Select
                            </Button>
                          )}
                        {employee.status === EmployeeStatus.selected &&
                          canDeselect && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<ThumbDown />}
                              onClick={() => handleDeselect(employee)}
                            >
                              {t("deselect")}
                            </Button>
                          )}
                        {canSelect && (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<Assignment />}
                            onClick={() => handleAddAgreement(employee)}
                          >
                            {t("addAgreement")}
                          </Button>
                        )}
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
        <FilterSidebar listType="candidates" />
      </Stack>

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="sm"
        fullWidth
      >
        {selected && (
          <DialogContent sx={{ p: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar src={selected.facePhoto} alt={fullName(selected)} />
                <Box>
                  <Typography variant="subtitle1">
                    {fullName(selected)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {summaryLine(selected)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {experience(selected)}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={() => setSelected(null)} aria-label="close">
                <Close />
              </IconButton>
            </Stack>
            <Box
              component="video"
              src={selected.employeeVideo}
              controls
              autoPlay
              playsInline
              sx={{ width: "100%", maxHeight: "70vh", bgcolor: "black" }}
            />
          </DialogContent>
        )}
      </Dialog>

      {openVisa && (
        <CustomDialog
          title={t("addAgreement")}
          isOpen={openVisa}
          handleDialogClose={closeVisa}
        >
          <VisaEntry
            currentEmployee={selectedEmployee}
            handleDialogClose={closeVisa}
          />
        </CustomDialog>
      )}
    </Box>
  );
}
